import { BUILD, DETAIL_PIECE_IDS, type DetailPiece } from "@eldrun/content";
import { CHUNK_SIZE, CHUNKS_PER_SIDE, WORLD_HALF } from "@eldrun/worldgen";
import { Box3, BufferGeometry, DynamicDrawUsage, Group, InstancedBufferAttribute, InstancedMesh, type BufferAttribute, type Object3D, type ShaderMaterial, type Vector4 } from "three";
import { detailMesh } from "../render/detail-meshes.ts";
import { models, type ModelLibrary } from "../render/model-library.ts";
import { QUALITY } from "../render/quality.ts";
import { withFarFade, withPalettes } from "../render/shaders/foliage.ts";
import { DETAIL_SUBCELL, PIECE_COUNT, SUBCELLS, type DetailChunk } from "../world/detail-scatter.ts";
import { paletteOf } from "./resource-layer.ts";

/** Alcance (m) dos enfeites na distância de visão padrão: 56, 80 e 104 m no Baixo, no Médio e no Alto. */
export const DETAIL_REACH = 80;
/** Começo do encolhimento, em fração do alcance: de 0,85 R a R as peças somem encolhendo. */
export const DETAIL_FADE_START = 0.85;
/** Instâncias no máximo por peça (ficam as subcélulas mais perto): 4096 pedrinhas e 1024 das outras. */
export const DETAIL_CAPS: Readonly<Record<DetailPiece, number>> = Object.fromEntries(DETAIL_PIECE_IDS.map((p) => [p, p === "pedrinha" ? 4096 : 1024])) as Record<DetailPiece, number>;
/** Célula (m) da câmera: os enfeites são recopiados quando ela muda. */
export const DETAIL_CAMERA_CELL = 8;
/** Chunks pedidos além do alcance (m): chegam antes de a câmera precisar deles. */
const PREFETCH = 64;
/** Chunks esquecidos além do alcance mais isto (m); voltam a ser pedidos se a câmera voltar. */
const FORGET = 320;

/** Enfeites de um chunk, pedidos ao worker de terreno dono dele. */
export type DetailSource = (cx: number, cz: number) => Promise<DetailChunk>;

export interface DetailLayerOptions {
  library?: ModelLibrary;
}

/** Um chunk recebido, com o tom e a paleta de cada instância já em float (prontos para copiar). */
interface Loaded {
  chunk: DetailChunk;
  colors: Float32Array;
  palettes: Float32Array;
  x0: number;
  z0: number;
}

/** Subcélula no alcance: inteira dentro dele (cópia em bloco) ou na borda (instância a instância). */
interface InRange {
  loaded: Loaded;
  sub: number;
  distance: number;
  inside: boolean;
}

/** Distância de (x, z) ao quadrado de lado `side` com canto em (x0, z0), e ao canto mais longe. */
function squareDistances(x: number, z: number, x0: number, z0: number, side: number): { near: number; far: number } {
  const dx = Math.max(x0 - x, 0, x - (x0 + side));
  const dz = Math.max(z0 - z, 0, z - (z0 + side));
  const fx = Math.max(Math.abs(x - x0), Math.abs(x - (x0 + side)));
  const fz = Math.max(Math.abs(z - z0), Math.abs(z - (z0 + side)));
  return { near: Math.hypot(dx, dz), far: Math.hypot(fx, fz) };
}

/** Chave da célula de construção (2 m) pelos índices. */
const cellKey = (i: number, j: number) => (i + 4096) * 8192 + (j + 4096);

/** Quanto (m) uma peça chega além do próprio centro, no máximo: meio tronco de 3,2 m e o toco. */
const PIECE_REACH = 2;

/** Cantos (x, z) da caixa de uma instância projetada no chão (rascunho, sem alocar por instância). */
const corners = new Float64Array(16);

/**
 * A caixa projetada (`corners`) cobre a célula (i, j)? Teste de eixos separadores: as arestas da caixa
 * projetada no chão vão na direção das colunas da matriz; as da célula, nos eixos (já testados pela
 * faixa de células da caixa).
 */
function overlapsCell(m: Float32Array, o: number, i: number, j: number): boolean {
  const x0 = i * BUILD.cell;
  const z0 = j * BUILD.cell;
  for (const c of [0, 4, 8]) {
    // Normal (no chão) da aresta ao longo da coluna c.
    const ax = -m[o + c + 2]!;
    const az = m[o + c]!;
    if (ax * ax + az * az < 1e-12) continue;
    let lo = Infinity;
    let hi = -Infinity;
    for (let n = 0; n < 16; n += 2) {
      const d = ax * corners[n]! + az * corners[n + 1]!;
      lo = Math.min(lo, d);
      hi = Math.max(hi, d);
    }
    let cellLo = Infinity;
    let cellHi = -Infinity;
    for (const [x, z] of [[x0, z0], [x0 + BUILD.cell, z0], [x0, z0 + BUILD.cell], [x0 + BUILD.cell, z0 + BUILD.cell]] as const) {
      const d = ax * x + az * z;
      cellLo = Math.min(cellLo, d);
      cellHi = Math.max(cellHi, d);
    }
    if (hi < cellLo || cellHi < lo) return false;
  }
  return true;
}

/**
 * Enfeites do chão (F44): pedrinhas, pedras soltas, lajes, mudas, troncos caídos e tábuas, só visuais.
 * O worker de terreno espalha cada chunk e devolve as matrizes prontas por subcélula de 32 m; aqui só
 * se copiam (`Float32Array.set`) as subcélulas no alcance, 80 m vezes a distância de visão, quando a
 * câmera troca de célula de 8 m, de perto para longe até o teto de cada peça. Uma `InstancedMesh` por
 * peça num grupo `detalhes` (no máximo 7 draw calls), sem LOD: entre 0,85 e 1 vez o alcance as peças
 * somem por pontilhado (`withFarFade`). A opção "Capim e detalhes" (`QUALITY.details`) esconde tudo no
 * Desligado e corta metade no Baixo, pela ordem de corte do worker. Construções do jogador tiram os
 * enfeites cuja malha entra na sua célula de 2 m; sem construção por perto, nem se confere.
 */
export class DetailLayer {
  readonly group = new Group();
  private readonly source: DetailSource;
  private readonly meshes: InstancedMesh[];
  private readonly palettes: (readonly string[])[];
  private readonly caps: number[];
  private readonly tris: number[];
  /** Caixa local da malha de cada peça (modelo ou reserva): o que a instância cobre no chão. */
  private readonly boxes: Box3[];
  private readonly fade: ShaderMaterial;
  private readonly paletteFade: ShaderMaterial;
  private readonly owned: BufferGeometry[] = [];
  private readonly loaded = new Map<number, Loaded>();
  private readonly pending = new Set<number>();
  private readonly failed = new Set<number>();
  /** Chunks que cobrem o alcance da última câmera (os que faltam deixam a camada incompleta). */
  private needed: number[] = [];
  private readonly structures = new Map<string, { x: number; z: number; cells: number[] }>();
  private readonly blocked = new Map<number, number>();
  private version = 0;
  private cameraKey = "";
  private lastKey = "";
  private disposed = false;

  constructor(root: Object3D, material: ShaderMaterial, source: DetailSource, options: DetailLayerOptions = {}) {
    const library = options.library ?? models.library;
    this.source = source;
    this.group.name = "detalhes";
    root.add(this.group);
    this.fade = withFarFade(material, DETAIL_REACH * DETAIL_FADE_START, DETAIL_REACH);
    this.paletteFade = withPalettes(this.fade);
    this.caps = DETAIL_PIECE_IDS.map((p) => DETAIL_CAPS[p]);
    this.palettes = [];
    this.tris = [];
    this.boxes = [];
    this.meshes = DETAIL_PIECE_IDS.map((piece, p) => {
      const source = detailMesh(piece, library);
      const cap = this.caps[p]!;
      let geometry = source.geometry;
      if (source.palettes.length > 1) {
        // Geometria própria que divide os buffers do modelo e ganha a paleta por instância (o bioma).
        geometry = new BufferGeometry();
        for (const [name, attribute] of Object.entries(source.geometry.attributes)) geometry.setAttribute(name, attribute);
        geometry.setIndex(source.geometry.index);
        geometry.boundingSphere = source.geometry.boundingSphere;
        geometry.setAttribute("palette", new InstancedBufferAttribute(new Float32Array(cap), 1).setUsage(DynamicDrawUsage));
        this.owned.push(geometry);
      }
      this.palettes.push(source.palettes);
      this.boxes.push(new Box3().setFromBufferAttribute(source.geometry.getAttribute("position") as BufferAttribute));
      this.tris.push((geometry.index ? geometry.index.count : geometry.getAttribute("position").count) / 3);
      const mesh = new InstancedMesh(geometry, source.palettes.length > 1 ? this.paletteFade : this.fade, cap);
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      mesh.instanceColor = new InstancedBufferAttribute(new Float32Array(cap * 3), 3).setUsage(DynamicDrawUsage);
      mesh.count = 0;
      mesh.visible = false;
      // As instâncias cercam a câmera: o corte é pelo alcance, não pelo frustum.
      mesh.frustumCulled = false;
      mesh.name = `detalhe-${piece}`;
      this.group.add(mesh);
      return mesh;
    });
  }

  /** Recebe os enfeites de um chunk (do worker, ou direto nos testes). */
  add(chunk: DetailChunk): void {
    if (this.disposed) return;
    const key = chunk.cz * CHUNKS_PER_SIDE + chunk.cx;
    const colors = new Float32Array(chunk.count * 3);
    const palettes = new Float32Array(chunk.count);
    for (let g = 0; g < SUBCELLS * SUBCELLS * PIECE_COUNT; g++) {
      const names = this.palettes[g % PIECE_COUNT]!;
      for (let k = chunk.offsets[g]!; k < chunk.offsets[g + 1]!; k++) {
        // Tom de ±8 % por instância; a paleta da muda pelo bioma (como as árvores).
        colors.fill(0.92 + (0.16 * chunk.tint[k]!) / 255, k * 3, k * 3 + 3);
        if (names.length > 1) palettes[k] = paletteOf(chunk.biome[k]!, names);
      }
    }
    this.pending.delete(key);
    this.loaded.set(key, { chunk, colors, palettes, x0: -WORLD_HALF + chunk.cx * CHUNK_SIZE, z0: -WORLD_HALF + chunk.cz * CHUNK_SIZE });
    this.version++;
  }

  /**
   * Estrutura do jogador (peça da grade ou estação posicionada): some o enfeite das células de 2 m que
   * ela cobre (a borda das paredes pega as duas vizinhas).
   */
  setStructure(id: string, x: number, z: number): void {
    this.removeStructure(id);
    const cells: number[] = [];
    const h = BUILD.cell / 2 - 0.01;
    for (const cx of new Set([Math.floor((x - h) / BUILD.cell), Math.floor((x + h) / BUILD.cell)])) {
      for (const cz of new Set([Math.floor((z - h) / BUILD.cell), Math.floor((z + h) / BUILD.cell)])) cells.push(cellKey(cx, cz));
    }
    for (const c of cells) this.blocked.set(c, (this.blocked.get(c) ?? 0) + 1);
    this.structures.set(id, { x, z, cells });
    this.version++;
  }

  removeStructure(id: string): void {
    const s = this.structures.get(id);
    if (!s) return;
    for (const c of s.cells) {
      const n = (this.blocked.get(c) ?? 1) - 1;
      if (n > 0) this.blocked.set(c, n);
      else this.blocked.delete(c);
    }
    this.structures.delete(id);
    this.version++;
  }

  /** Pede os chunks que faltam e recopia as instâncias se a câmera trocou de célula de 8 m ou algo mudou. */
  update(cameraX: number, cameraZ: number): void {
    const density = Math.max(0, Math.min(1, QUALITY.details));
    this.group.visible = density > 0;
    if (density <= 0 || this.disposed) return;
    const reach = DETAIL_REACH * QUALITY.distance;
    const band = this.fade.uniforms["uFarFade"]!.value as Vector4;
    band.z = reach * DETAIL_FADE_START;
    band.w = reach;
    const cell = DETAIL_CAMERA_CELL;
    const ix = Math.floor((cameraX + WORLD_HALF) / cell);
    const iz = Math.floor((cameraZ + WORLD_HALF) / cell);
    // Do centro da célula, o alcance cresce meia diagonal: vale de qualquer ponto dela.
    const cx = -WORLD_HALF + (ix + 0.5) * cell;
    const cz = -WORLD_HALF + (iz + 0.5) * cell;
    const radius = reach + cell * Math.SQRT1_2;
    const cameraKey = `${ix},${iz},${reach}`;
    if (cameraKey !== this.cameraKey) {
      this.cameraKey = cameraKey;
      this.fetch(cx, cz, radius);
    }
    const key = `${cameraKey},${density},${this.version}`;
    if (key === this.lastKey) return;
    this.lastKey = key;
    this.refresh(cx, cz, radius, density);
  }

  /** Pede ao worker os chunks perto do alcance e esquece os longe. */
  private fetch(x: number, z: number, radius: number): void {
    this.needed = [];
    for (let cz = 0; cz < CHUNKS_PER_SIDE; cz++) {
      for (let cx = 0; cx < CHUNKS_PER_SIDE; cx++) {
        const key = cz * CHUNKS_PER_SIDE + cx;
        const d = squareDistances(x, z, -WORLD_HALF + cx * CHUNK_SIZE, -WORLD_HALF + cz * CHUNK_SIZE, CHUNK_SIZE).near;
        if (d <= radius) this.needed.push(key);
        if (d > radius + FORGET) {
          if (this.loaded.delete(key)) this.version++;
          continue;
        }
        if (d > radius + PREFETCH || this.loaded.has(key) || this.pending.has(key) || this.failed.has(key)) continue;
        this.pending.add(key);
        this.source(cx, cz).then(
          (chunk) => {
            // Chegou depois de esquecido (a câmera já foi embora): não guarda.
            if (this.pending.has(key)) this.add(chunk);
          },
          () => {
            // Pool encerrado ou com falha: o terreno mostra o erro; aqui só não pede de novo.
            this.pending.delete(key);
            this.failed.add(key);
          },
        );
      }
    }
    for (const key of this.pending) {
      const cx = key % CHUNKS_PER_SIDE;
      const cz = Math.floor(key / CHUNKS_PER_SIDE);
      if (squareDistances(x, z, -WORLD_HALF + cx * CHUNK_SIZE, -WORLD_HALF + cz * CHUNK_SIZE, CHUNK_SIZE).near > radius + FORGET) this.pending.delete(key);
    }
  }

  /** Copia as subcélulas no alcance, das mais perto para as mais longe, até o teto de cada peça. */
  private refresh(x: number, z: number, radius: number, density: number): void {
    const threshold = Math.round(density * 256);
    const counts = new Array<number>(PIECE_COUNT).fill(0);
    const list: InRange[] = [];
    for (const loaded of this.loaded.values()) {
      if (squareDistances(x, z, loaded.x0, loaded.z0, CHUNK_SIZE).near > radius) continue;
      for (let sub = 0; sub < SUBCELLS * SUBCELLS; sub++) {
        const d = squareDistances(x, z, loaded.x0 + (sub % SUBCELLS) * DETAIL_SUBCELL, loaded.z0 + Math.floor(sub / SUBCELLS) * DETAIL_SUBCELL, DETAIL_SUBCELL);
        if (d.near <= radius) list.push({ loaded, sub, distance: d.near, inside: d.far <= radius });
      }
    }
    list.sort((a, b) => a.distance - b.distance);
    // Só confere construções se alguma está ao alcance de uma peça desenhada (o caso comum é nenhuma).
    let near = false;
    for (const s of this.structures.values()) if ((s.x - x) ** 2 + (s.z - z) ** 2 <= (radius + BUILD.cell + PIECE_REACH) ** 2) near = true;
    const r2 = radius * radius;
    for (const { loaded, sub, inside } of list) {
      const { chunk } = loaded;
      for (let p = 0; p < PIECE_COUNT; p++) {
        const g = sub * PIECE_COUNT + p;
        const start = chunk.offsets[g]!;
        let end = start;
        // Corte de densidade: o grupo vem em ordem crescente de `rank`.
        while (end < chunk.offsets[g + 1]! && chunk.rank[end]! < threshold) end++;
        const room = this.caps[p]! - counts[p]!;
        if (end === start || room <= 0) continue;
        const mesh = this.meshes[p]!;
        if (inside && !near) {
          const n = Math.min(end - start, room);
          this.copy(mesh, loaded, start, start + n, counts[p]!);
          counts[p]! += n;
          continue;
        }
        for (let k = start; k < end && counts[p]! < this.caps[p]!; k++) {
          const m = chunk.matrices;
          const dx = m[k * 16 + 12]! - x;
          const dz = m[k * 16 + 14]! - z;
          if (!inside && dx * dx + dz * dz > r2) continue;
          if (near && this.underStructure(m, k, p)) continue;
          this.copy(mesh, loaded, k, k + 1, counts[p]!);
          counts[p]! += 1;
        }
      }
    }
    this.meshes.forEach((mesh, p) => {
      const n = counts[p]!;
      mesh.count = n;
      // Malha sem instância sai da lista de desenho.
      mesh.visible = n > 0;
      if (n === 0) return;
      const attributes = [mesh.instanceMatrix, mesh.instanceColor!, mesh.geometry.getAttribute("palette") as InstancedBufferAttribute | undefined];
      for (const attribute of attributes) {
        if (!attribute) continue;
        // Sobe só o trecho usado.
        attribute.clearUpdateRanges();
        attribute.addUpdateRange(0, n * attribute.itemSize);
        attribute.needsUpdate = true;
      }
    });
  }

  private copy(mesh: InstancedMesh, loaded: Loaded, from: number, to: number, at: number): void {
    (mesh.instanceMatrix.array as Float32Array).set(loaded.chunk.matrices.subarray(from * 16, to * 16), at * 16);
    (mesh.instanceColor!.array as Float32Array).set(loaded.colors.subarray(from * 3, to * 3), at * 3);
    const palette = mesh.geometry.getAttribute("palette");
    if (palette) (palette.array as Float32Array).set(loaded.palettes.subarray(from, to), at);
  }

  /**
   * A malha da instância cobre alguma célula com construção? A caixa local da peça, posta pela matriz
   * (escala, giro e inclinação) e projetada no chão: das células que ela alcança, só as bloqueadas são
   * conferidas, pelos eixos separadores. Não basta o centro: a copa da muda e a ponta do tronco entram
   * na célula vizinha.
   */
  private underStructure(m: Float32Array, k: number, p: number): boolean {
    const o = k * 16;
    const box = this.boxes[p]!;
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (let c = 0; c < 8; c++) {
      const lx = c & 1 ? box.max.x : box.min.x;
      const ly = c & 2 ? box.max.y : box.min.y;
      const lz = c & 4 ? box.max.z : box.min.z;
      const x = m[o + 12]! + m[o]! * lx + m[o + 4]! * ly + m[o + 8]! * lz;
      const z = m[o + 14]! + m[o + 2]! * lx + m[o + 6]! * ly + m[o + 10]! * lz;
      corners[c * 2] = x;
      corners[c * 2 + 1] = z;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minZ = Math.min(minZ, z);
      maxZ = Math.max(maxZ, z);
    }
    for (let i = Math.floor(minX / BUILD.cell); i <= Math.floor(maxX / BUILD.cell); i++) {
      for (let j = Math.floor(minZ / BUILD.cell); j <= Math.floor(maxZ / BUILD.cell); j++) {
        if (this.blocked.has(cellKey(i, j)) && overlapsCell(m, o, i, j)) return true;
      }
    }
    return false;
  }

  /** Instâncias desenhadas agora. */
  get drawn(): number {
    return this.group.visible ? this.meshes.reduce((n, mesh) => n + mesh.count, 0) : 0;
  }

  /** Malhas com algo a desenhar (cada uma é um draw call): no máximo 7. */
  get drawCalls(): number {
    return this.group.visible ? this.meshes.filter((m) => m.count > 0).length : 0;
  }

  /** Triângulos por quadro (instâncias vezes os triângulos da peça). */
  get triangles(): number {
    return this.group.visible ? this.meshes.reduce((n, mesh, p) => n + mesh.count * this.tris[p]!, 0) : 0;
  }

  /** Instâncias por peça agora (para os testes e o diagnóstico). */
  counts(): Record<DetailPiece, number> {
    return Object.fromEntries(DETAIL_PIECE_IDS.map((p, i) => [p, this.group.visible ? this.meshes[i]!.count : 0])) as Record<DetailPiece, number>;
  }

  /** Todos os chunks do alcance da última câmera chegaram (ou falharam) e já foram copiados; desligada, sempre. */
  get complete(): boolean {
    if (!this.group.visible) return true;
    return this.lastKey !== "" && this.needed.every((key) => this.loaded.has(key) || this.failed.has(key)) && this.lastKey.endsWith(`,${this.version}`);
  }

  stats(): { drawn: number; drawCalls: number; triangles: number; chunks: number; pending: number; structures: number } {
    return { drawn: this.drawn, drawCalls: this.drawCalls, triangles: this.triangles, chunks: this.loaded.size, pending: this.pending.size, structures: this.structures.size };
  }

  dispose(): void {
    this.disposed = true;
    this.group.removeFromParent();
    for (const mesh of this.meshes) mesh.dispose();
    // Libera os buffers divididos com a biblioteca: o three os sobe de novo se outra camada usar.
    for (const geometry of this.owned) geometry.dispose();
    this.fade.dispose();
    this.paletteFade.dispose();
    this.loaded.clear();
    this.pending.clear();
  }
}
